import { createContext, ReactNode, useContext, useState, useMemo } from "react";
import { useFormTyped } from "shared/hooks";
import { CreateCRMUnitRequest, UnitFormData } from "./types";
import { unitValidationSchema } from "./validation";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";

const DEFAULT_FORM_VALUES: UnitFormData = {
  title: "",
  externalId: "",
  description: "",

  monthly_rent_amount: undefined,
  home_rent_amount: undefined,
  site_rent_amount: undefined,
  available_at: new Date().toISOString().split("T")[0],
  occupied: false,

  sqft: undefined,
  beds: undefined,
  baths: undefined,

  amenities: [],
  media: []
};

interface UnitProviderProps {
  children: ReactNode;
  propertyId?: string;
}

interface CreateUnitResponse {
  id: string;
  [key: string]: any;
}

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

const buildRentFields = (
  formData: UnitFormData,
  homeSiteRentEnabled: boolean
) => {
  if (
    homeSiteRentEnabled &&
    formData.home_rent_amount !== undefined &&
    formData.site_rent_amount !== undefined
  ) {
    const homeCents = Math.round(
      parseFloat(String(formData.home_rent_amount)) * 100
    );
    const siteCents = Math.round(
      parseFloat(String(formData.site_rent_amount)) * 100
    );
    return {
      monthly_rent_amount: {
        cents: homeCents + siteCents,
        currency: "USD"
      },
      home_rent_amount: { cents: homeCents, currency: "USD" },
      site_rent_amount: { cents: siteCents, currency: "USD" }
    };
  }

  return {
    monthly_rent_amount:
      formData.monthly_rent_amount && formData.monthly_rent_amount > 0
        ? {
            cents: Math.round(formData.monthly_rent_amount * 100),
            currency: "USD"
          }
        : undefined
  };
};

const transformToAPIData = (
  formData: UnitFormData,
  homeSiteRentEnabled: boolean = false
): CreateCRMUnitRequest => {
  const rentFields = buildRentFields(formData, homeSiteRentEnabled);

  return {
    media_session_key: sessionKey,
    title: formData.title,
    external_id: formData.externalId || undefined,
    description: formData.description || undefined,
    ...rentFields,
    available_at: formData.available_at || undefined,
    occupied: formData.occupied ?? false,
    plan: {
      sqft: formData.sqft && formData.sqft > 0 ? formData.sqft : undefined,
      beds: formData.beds && formData.beds > 0 ? formData.beds : undefined,
      baths: formData.baths && formData.baths > 0 ? formData.baths : undefined
    },
    amenity_ids: formData.amenities,
    media: formData.media
  };
};

interface UnitFormContextType {
  values: UnitFormData;
  errors: Partial<Record<keyof UnitFormData, string>>;
  isSubmitting: boolean;
  register: any;
  handleSubmit: (e: React.FormEvent) => void;
  handleChange: (
    key: keyof UnitFormData,
    customMap?: (value: any, prev: any) => any,
    mask?: string
  ) => (e: any) => void;
  sessionKey: string;
  canSubmit: boolean;
  setPhotosUploading: (isUploading: boolean) => void;
}

const UnitFormContext = createContext<UnitFormContextType | undefined>(
  undefined
);

export const UnitFormProvider = ({
  children,
  propertyId
}: UnitProviderProps) => {
  const navigate = useNavigate();
  const { pms, type } = useAuthContext();
  const [photosUploading, setPhotosUploading] = useState(false);
  const { values, register, onSubmit, handleChange, isSubmitting, errors } =
    useFormTyped<UnitFormData>(DEFAULT_FORM_VALUES, unitValidationSchema);

  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  const canSubmit = useMemo(() => {
    return !photosUploading;
  }, [photosUploading]);

  const { mutate: createUnit, isPending: isCreatingUnit } = useMutation({
    mutationFn: (data: CreateCRMUnitRequest) =>
      instance.post<CreateUnitResponse>(
        `/portal/v1/properties/${propertyId}/units`,
        data
      ),
    onSuccess: ({ data }) => {
      toast.success("Unit created successfully!");
      navigate(`/properties/${propertyId}/units/${data.id}`);
    },
    onError: (error) => {
      toast.error("Error creating unit: " + getErrorMessage(error));
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (formData) => {
      if (!propertyId) {
        toast.error("Property ID is required");
        return;
      }

      const data = transformToAPIData(formData, homeSiteRentEnabled);

      createUnit(data);
    },
    onError: () => {
      toast.error("Form validation errors");
    }
  });

  const contextValue: UnitFormContextType = {
    values,
    isSubmitting: isSubmitting || isCreatingUnit,
    register,
    handleSubmit,
    handleChange,
    errors,
    sessionKey,
    canSubmit,
    setPhotosUploading
  };

  return (
    <UnitFormContext.Provider value={contextValue}>
      {children}
    </UnitFormContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUnitFormContext = () => {
  const context = useContext(UnitFormContext);
  if (context === undefined)
    throw new Error(
      "useUnitFormContext must be used within a UnitFormProvider"
    );
  return context;
};
